import { useEffect, useState } from "react";
import { Alert, Avatar, Dropdown, Form, Grid, Input, Layout, Menu, message, Modal, Space } from "antd";
import type { MenuProps } from "antd";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  BankOutlined,
  BarChartOutlined,
  CalendarOutlined,
  GiftOutlined,
  LinkOutlined,
  LockOutlined,
  LogoutOutlined,
  QuestionCircleOutlined,
  RocketOutlined,
  SearchOutlined,
  TeamOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { useAuth } from "../auth/AuthContext";
import { api, apiErrorMessage } from "../api/client";
import { getGroup } from "../api/admin";

const { Header, Sider, Content } = Layout;

function safeStorage(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const screens = Grid.useBreakpoint();
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [groupStatus, setGroupStatus] = useState<"DEMO" | "LIVE" | null>(null);

  // The Demo banner belongs to a pantry, not the whole system: it disappears once the pantry being worked in
  // is LIVE. (SUPER_ADMIN / FOOD_BANK_ADMIN pick their pantry via localStorage - re-checked on every navigation.)
  const bannerGroupId = user ? (user.groupId ?? safeStorage("pantry_active_group_id")) : null;
  useEffect(() => {
    if (!bannerGroupId || bannerGroupId === "ALL") {
      setGroupStatus(null);
      return;
    }
    let cancelled = false;
    getGroup(bannerGroupId)
      .then((g) => !cancelled && setGroupStatus(g.status ?? "DEMO"))
      .catch(() => !cancelled && setGroupStatus(null));
    return () => {
      cancelled = true;
    };
  }, [bannerGroupId, location.pathname]);

  if (!user) return null;

   const items: MenuProps["items"] = [
    { key: "/clients", icon: <SearchOutlined />, label: "Find Client" },
    { key: "/today", icon: <CalendarOutlined />, label: "Today" },
    { key: "/donations", icon: <GiftOutlined />, label: "Donations" },
    { key: "/resources", icon: <LinkOutlined />, label: "Resources" },
    { key: "/reports", icon: <BarChartOutlined />, label: "Reports" },
    { key: "/support", icon: <QuestionCircleOutlined />, label: "Support" },
  ];

  if (user.role !== "STAFF") {
    items.push(
      { key: "/admin/groups", icon: <TeamOutlined />, label: "Groups" },
      { key: "/admin/users", icon: <UserOutlined />, label: "Users" }
    );
  }
  if (user.role === "SUPER_ADMIN") {
    items.push(
      { key: "/admin/food-banks", icon: <BankOutlined />, label: "Food Banks" },
      { key: "/admin/go-live", icon: <RocketOutlined />, label: "Go Live" }
    );
  }

  // Highlight the closest matching top-level item for nested routes (e.g. /clients/:id -> /clients).
  const selectedKey = items.find((item) => item && location.pathname.startsWith(String(item.key)))?.key as string | undefined;

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider breakpoint="lg" collapsedWidth={screens.xs ? 0 : 80}>
       <div style={{ color: "#fff", padding: 16, fontWeight: 600, fontSize: 16 }}>PantrySystems 2.0</div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKey ? [selectedKey] : []}
          items={items}
          onClick={({ key }) => navigate(key)}
        />
      </Sider>
      <Layout>
        <Header style={{ background: "#fff", padding: "0 16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontWeight: 500 }}>
            {user.role === "SUPER_ADMIN" ? "Administrator" : user.role === "FOOD_BANK_ADMIN" ? user.foodBankName : user.groupName}
          </span>
                    <Dropdown
            menu={{
              items: [
                ...(user.canChangePassword
                  ? [
                      { key: "change-password", icon: <LockOutlined />, label: "Change Password" },
                      { type: "divider" as const },
                    ]
                  : []),
                { key: "logout", icon: <LogoutOutlined />, label: "Log out" },
              ],
              onClick: ({ key }) => {
                if (key === "logout") logout();
                if (key === "change-password") setChangePasswordOpen(true);
              },
            }}
          >
            <Space style={{ cursor: "pointer" }}>
              <Avatar icon={<UserOutlined />} size="small" />
              {`${user.firstName} ${user.lastName}`}
            </Space>
          </Dropdown>
        </Header>
                                <Content style={{ margin: 16 }}>
          {groupStatus === "DEMO" && (
            <Alert
              type="warning"
              showIcon
              banner
              message="Demo System: not to be used for real transactions yet. This pantry will switch to the live system on its go-live date."
              style={{ marginBottom: 16 }}
            />
          )}
          <Outlet />
        </Content>
      </Layout>
      <ChangePasswordModal open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
    </Layout>
  );
}

/** Self-service password change, reachable from the header dropdown for any logged-in user regardless of role. */
function ChangePasswordModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  async function handleOk() {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      await api.post("/auth/change-password", {
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
      });
      message.success("Password changed.");
      form.resetFields();
      onClose();
    } catch (err) {
      if (err && typeof err === "object" && "errorFields" in err) return;
      message.error(apiErrorMessage(err, "Could not change password"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      title="Change Password"
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={handleOk}
      confirmLoading={submitting}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Form.Item name="currentPassword" label="Current password" rules={[{ required: true, message: "Enter your current password" }]}>
          <Input.Password autoComplete="current-password" autoFocus />
        </Form.Item>
        <Form.Item
          name="newPassword"
          label="New password"
          rules={[
            { required: true, message: "Enter a new password" },
            { min: 8, message: "At least 8 characters" },
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
        <Form.Item
          name="confirmPassword"
          label="Confirm new password"
          dependencies={["newPassword"]}
          rules={[
            { required: true, message: "Confirm your new password" },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("newPassword") === value) return Promise.resolve();
                return Promise.reject(new Error("Passwords do not match"));
              },
            }),
          ]}
        >
          <Input.Password autoComplete="new-password" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
